import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  FloatNumberInput,
  IntNumberInput,
  Value,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import {
  useQpmOfferShowQuery,
  useQpmOfferUpdateHirePurchaseMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  HirePurchase,
  UpdateHirePurchaseRequest,
} from "@/services/backend/qpm/offers/types/offer";
import { useState } from "react";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { MonitorType } from "@/services/backend/qpm/types/enums";

export type HirePurchaseTableComponentProps = {
  offerId: string;
};

export function HirePurchaseTableComponent({
  offerId,
}: HirePurchaseTableComponentProps) {
  const {
    data: offer,
    isLoading,
    error,
  } = useQpmOfferShowQuery({ id: offerId });

  if (isLoading) {
    return <HirePurchaseTableRowSkeleton />;
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <Table className="min-w-[1020px]">
      <TableHeader>
        <TableRow className="bg-muted/30">
          <TableHead className="h-10 px-2 py-2 font-semibold">
            {t("Position")}
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-center font-semibold">
            {t("Aktiv")}
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-center font-semibold">
            {t("Optional")}
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-end font-semibold">
            {t("Anzahl")}
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-end font-semibold">
            {t("Anschaffungspreis")}
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-end font-semibold">
            {t("Aufschlag")} %
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-end font-semibold">
            {t("Gesamtpreis")}
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-end font-semibold">
            {t("Laufzeit")}
          </TableHead>
          <TableHead className="h-10 px-2 py-2 text-end font-semibold">
            {t("Monatsrate")}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading && <HirePurchaseTableRowSkeleton />}
        {!isLoading && offer && (
          <HirePurchaseTableRow
            offerId={offer.id}
            data={offer.hirePurchaseFiv}
            monitorType={MonitorType.FlyExterminator}
            immutable={offer.immutable}
          />
        )}
      </TableBody>
    </Table>
  );
}

export type HirePurchaseTableRowProps = {
  offerId: string;
  data: HirePurchase;
  monitorType: MonitorType;
  immutable?: boolean;
};

export function HirePurchaseTableRow({
  offerId,
  data,
  monitorType,
  immutable,
}: HirePurchaseTableRowProps) {
  const [request, setRequest] = useState<UpdateHirePurchaseRequest>({
    offerId,
    isActive: data.isActive,
    isOptional: data.isOptional,
    monitorType: data.monitorType,
    surcharge: data.surcharge,
    durationMonths: data.durationMonths,
  });
  const [setHirePurchase, { error, isLoading, isSuccess, reset }] =
    useQpmOfferUpdateHirePurchaseMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setHirePurchase,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
    { toastError: true },
  );

  const handleSurcharge = (value: number) => {
    let surcharge = 0;
    if (value > 0) {
      surcharge = value / 100;
    }
    setRequest({ ...request, surcharge });
  };

  return (
    <TableRow key={monitorType} className="border-none">
      <TableCell className="px-2 py-2.5 text-sm font-medium sm:text-base">
        {MonitorTypeUtil.toString(data.monitorType as MonitorType)}
      </TableCell>
      <TableCell
        role="checkbox"
        className="px-2 py-2.5 text-center font-medium"
      >
        <Checkbox
          checked={data.isActive}
          onCheckedChange={(newVal) => {
            if (immutable) {
              return;
            }
            setRequest({ ...request, isActive: newVal as boolean });
          }}
          disabled={isLoading || immutable}
        />
      </TableCell>
      <TableCell
        role="checkbox"
        className="px-2 py-2.5 text-center font-medium"
      >
        <Checkbox
          checked={data.isActive && data.isOptional}
          onCheckedChange={(newVal) => {
            if (immutable) {
              return;
            }
            setRequest({ ...request, isOptional: newVal as boolean });
          }}
          disabled={isLoading || immutable || !data.isActive}
        />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end font-medium">
        <Value
          value={data.monitorAmount}
          fractions={0}
          className="tabular-nums"
        />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end">
        <Value value={data.totalCost} fractions={2} className="tabular-nums" />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end">
        <FloatNumberInput
          className="ml-auto w-24"
          value={data.surcharge * 100}
          onChange={(newVal) => {
            if (immutable) {
              return;
            }
            handleSurcharge(newVal);
          }}
          disabled={isLoading || immutable || !data.isActive}
        />
      </TableCell>

      <TableCell className="px-2 py-2.5 text-end">
        <Value
          value={data.totalCostSurcharged}
          fractions={2}
          className="tabular-nums"
        />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end">
        <IntNumberInput
          className="ml-auto w-20"
          value={data.durationMonths}
          onChange={(newVal) => {
            if (immutable) {
              return;
            }
            setRequest({ ...request, durationMonths: newVal });
          }}
          disabled={isLoading || immutable || !data.isActive}
        />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end">
        <Value
          value={data.ratePerMonth}
          fractions={2}
          className="tabular-nums"
        />
      </TableCell>
    </TableRow>
  );
}

export function HirePurchaseTableRowSkeleton() {
  return (
    <TableRow>
      <TableCell className="px-2 py-2.5 text-base font-medium">
        <Skeleton className="h-8 w-full" />
      </TableCell>
      <TableCell
        role="checkbox"
        className="px-2 py-2.5 text-center font-medium"
      >
        <Skeleton className="h-8 w-full" />
      </TableCell>
      <TableCell
        role="checkbox"
        className="px-2 py-2.5 text-center font-medium"
      >
        <Skeleton className="h-8 w-full" />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end font-medium">
        <Skeleton className="h-8 w-full" />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end">
        <Skeleton className="h-8 w-full" />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end">
        <Skeleton className="h-8 w-full" />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end">
        <Skeleton className="h-8 w-full" />
      </TableCell>
      <TableCell className="px-2 py-2.5 text-end">
        <Skeleton className="h-8 w-full" />
      </TableCell>
    </TableRow>
  );
}
